/**
 * Hand-drawn 24x24 icon set for the toolbars. Everything is stroke based and inherits
 * `currentColor`, so active/disabled states come from CSS.
 */
function icon(body: string): string {
  return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">${body}</svg>`;
}

const DOT = (x: number, y: number): string =>
  `<circle cx="${x}" cy="${y}" r="1.7" fill="currentColor" stroke="none"/>`;

export const ICONS = {
  line: icon(`<path d="M4.5 19.5 L19.5 4.5"/>${DOT(4.5, 19.5)}${DOT(19.5, 4.5)}`),

  polyline: icon(
    `<path d="M3 18.5 L9 7.5 L15 14 L21 5"/>${DOT(3, 18.5)}${DOT(9, 7.5)}${DOT(15, 14)}${DOT(21, 5)}`,
  ),

  rect: icon(`<path d="M4 6 H20 V18 H4 Z"/>`),

  spline: icon(
    `<path d="M3.5 18 C 7 18, 8 8, 12 8 S 17 15, 20.5 15"/>` +
      `<path d="M3.5 18 L8 15 L12 8 L17 12 L20.5 15" opacity="0.35" stroke-dasharray="1.5 1.5"/>` +
      `${DOT(3.5, 18)}${DOT(8, 15)}${DOT(12, 8)}${DOT(17, 12)}${DOT(20.5, 15)}`,
  ),

  circle: icon(`<circle cx="12" cy="12" r="8"/>${DOT(12, 12)}<path d="M12 2.5 V5 M12 19 V21.5 M2.5 12 H5 M19 12 H21.5" opacity="0.55"/>`),

  arc: icon(`<path d="M4 17.5 A9 9 0 0 1 20 17.5"/>${DOT(4, 17.5)}${DOT(20, 17.5)}`),

  text: icon(`<path d="M5.5 19 L12 5 L18.5 19"/><path d="M8.4 13.6 H15.6"/>`),

  hatch: icon(
    `<path d="M4 4 H20 V20 H4 Z"/><path d="M4 12 L12 4 M4 20 L20 4 M12 20 L20 12" opacity="0.7"/>`,
  ),

  dim: icon(
    `<path d="M4.5 5 V19.5 M19.5 5 V19.5 M4.5 15 H19.5"/>` +
      `<path d="M4.5 15 L8 13.4 M4.5 15 L8 16.6 M19.5 15 L16 13.4 M19.5 15 L16 16.6"/>`,
  ),

  daligned: icon(
    `<path d="M4 20 V12 M20 12 V4 M4 15.5 L20 8.5"/>` +
      `<path d="M4 15.5 L7.4 13.6 M4 15.5 L7.5 17 M20 8.5 L16.6 10.4 M20 8.5 L16.5 6.9"/>`,
  ),

  erase: icon(`<path d="M8 19.5 L3.8 15.3 L13.2 5.9 L17.4 10.1 Z"/><path d="M9.5 19.5 H20.5"/>`),

  move: icon(
    `<path d="M12 3 V21 M3 12 H21"/>` +
      `<path d="M9.6 5.4 L12 3 L14.4 5.4 M9.6 18.6 L12 21 L14.4 18.6 M5.4 9.6 L3 12 L5.4 14.4 M18.6 9.6 L21 12 L18.6 14.4"/>`,
  ),

  copy: icon(`<path d="M4 9 H14 V20 H4 Z"/><path d="M8 5 H20 V16 H17"/>`),

  rotate: icon(
    `<path d="M19.5 12 A7.5 7.5 0 1 1 12 4.5"/><path d="M12 4.5 L15.5 2.4 M12 4.5 L15.5 6.6"/>`,
  ),

  scale: icon(`<path d="M4 20 V9 H11"/><path d="M8.5 15.5 L19 5"/><path d="M19 5 H14.6 M19 5 V9.4"/>`),

  mirror: icon(
    `<path d="M9.5 5 L9.5 19 L3 19 Z"/><path d="M14.5 5 L14.5 19 L21 19 Z"/>` +
      `<path d="M12 2.5 V21.5" stroke-dasharray="2 2" opacity="0.7"/>`,
  ),

  offset: icon(`<path d="M4 4 H20 V20 H4 Z"/><path d="M7.5 7.5 H16.5 V16.5 H7.5 Z"/>`),

  trim: icon(`<path d="M3.5 12 H10 M14 12 H20.5"/><path d="M12 3 V21"/><path d="M10 12 L12 10 M12 10 L14 12"/>`),

  extend: icon(`<path d="M3.5 12 H14"/><path d="M14 12 L10.5 10 M14 12 L10.5 14"/><path d="M18 4.5 V19.5"/>`),

  fillet: icon(`<path d="M4 20 V9 A5 5 0 0 1 9 4 H20"/><path d="M4 13 H9 V4" opacity="0.45"/>`),

  chamfer: icon(`<path d="M4 20 V10 L10 4 H20"/><path d="M4 10 H10 V4" opacity="0.45"/>`),

  undo: icon(`<path d="M9 7.5 H14.5 A5 5 0 0 1 14.5 17.5 H8"/><path d="M9 7.5 L12 4.5 M9 7.5 L12 10.5"/>`),

  redo: icon(`<path d="M15 7.5 H9.5 A5 5 0 0 0 9.5 17.5 H16"/><path d="M15 7.5 L12 4.5 M15 7.5 L12 10.5"/>`),

  zoomExt: icon(
    `<circle cx="10.5" cy="10.5" r="6"/><path d="M15 15 L20.5 20.5"/>` +
      `<path d="M4 3 H8 M3 4 V8 M20 3 H16 M21 4 V8" opacity="0.55"/>`,
  ),

  nodes: icon(
    `<rect x="3" y="4" width="6" height="6" rx="1"/><rect x="15" y="14" width="6" height="6" rx="1"/>` +
      `<path d="M9 7 H15 A3 3 0 0 1 18 10 V14"/>`,
  ),

  split: icon(`<rect x="3" y="4.5" width="18" height="15" rx="1"/><path d="M12 4.5 V19.5"/>`),

  window: icon(`<rect x="3" y="7" width="12" height="12" rx="1"/><path d="M12 12 L20 4 M20 4 H15.6 M20 4 V8.4"/>`),

  newFile: icon(
    `<path d="M6 3 H14 L19 8 V21 H6 Z"/><path d="M14 3 V8 H19"/><path d="M12 12 V18 M9 15 H15"/>`,
  ),

  open: icon(`<path d="M3 7 H9 L11 9.5 H21 L18.5 20 H3 Z"/><path d="M3 7 V5 H9 L11 7"/>`),

  folder: icon(`<path d="M3 6 H10 L12 8.5 H21 V19 H3 Z"/><path d="M12 12 V16 M10 14 H14"/>`),

  unlink: icon(
    `<path d="M10 13.5 L13.5 10 A4 4 0 0 1 19.2 15.7 L16.7 18.2 A4 4 0 0 1 11 18.2"/>` +
      `<path d="M14 10.5 L10.5 14 A4 4 0 0 1 4.8 8.3 L7.3 5.8 A4 4 0 0 1 13 5.8"/>` +
      `<path d="M3 21 L21 3"/>`,
  ),

  save: icon(
    `<path d="M5 4 H16 L20 8 V20 H5 Z"/><path d="M8 4 V10 H16 V4"/><rect x="8" y="13" width="9" height="5"/>`,
  ),

  export: icon(`<path d="M12 3 V14"/><path d="M8.5 10.5 L12 14 L15.5 10.5"/><path d="M4 17 V20 H20 V17"/>`),

  constraint: icon(
    `<rect x="3.5" y="15" width="5" height="5" rx="1"/><circle cx="19" cy="6" r="2"/>` +
      `<path d="M6 15 V9 A3 3 0 0 1 9 6 H17"/>`,
  ),
} as const;

export type IconName = keyof typeof ICONS;
